import { Control, type ControlOptions } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import type { Rect, Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { drawAlignedText } from "./Label.js";

export interface ToggleOptions extends ControlOptions {
  text?: string;
  font?: FontSpec;
  checked?: boolean;
  /** Put the box on the right of the caption, as in right-to-left forms. */
  boxOnRight?: boolean;
  onChange?: () => void;
}

/**
 * Shared behavior for CheckBox and RadioButton: a glyph box, a caption
 * beside it, and Space to toggle. The whole control is the click target, the
 * way Windows does it.
 */
export abstract class ToggleControl extends Control {
  #text: string;
  #font: FontSpec | null;
  protected checkedValue = false;

  boxOnRight: boolean;

  constructor(options: ToggleOptions = {}) {
    super({ width: 160, height: 22, tabStop: true, ...options });
    this.#text = options.text ?? "";
    this.#font = options.font ?? null;
    this.checkedValue = options.checked ?? false;
    this.boxOnRight = options.boxOnRight ?? false;
    if (options.onChange) this.on("change", options.onChange);

    this.on("click", (e) => this.#onClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    if (this.#text === v) return;
    this.#text = v;
    this.emit("textchanged");
    this.invalidateMeasure();
    this.invalidate();
  }

  get font(): FontSpec | null {
    return this.#font;
  }
  set font(v: FontSpec | null) {
    this.#font = v;
    this.invalidateMeasure();
    this.invalidate();
  }

  get checked(): boolean {
    return this.checkedValue;
  }
  set checked(v: boolean) {
    this.setChecked(v);
  }

  /** Subclasses override to enforce grouping rules. */
  protected setChecked(value: boolean): void {
    if (this.checkedValue === value) return;
    this.checkedValue = value;
    this.emit("change");
    this.invalidate();
  }

  /** What clicking or pressing Space does. */
  protected abstract toggle(): void;

  /** Size of the glyph box for the current theme. */
  protected abstract boxSize(theme: Theme): number;

  protected abstract drawBox(rc: RenderContext, r: Rect, theme: Theme, s: VisualState): void;

  #onClick(e: MouseEventArgs): void {
    if (!this.effectivelyEnabled) return;
    this.focus();
    this.toggle();
    e.handled = true;
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (e.key !== " ") return;
    this.toggle();
    e.handled = true;
  }

  override visualState(): VisualState {
    return { ...super.visualState(), checked: this.checkedValue };
  }

  /** The box, the gap and the caption — the same three parts `layoutParts` lays out. */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    const theme = desktop.theme;
    const box = this.boxSize(theme);
    const font = this.#font ?? theme.metrics.font;
    const line = desktop.rc.lineHeight(font);
    if (!this.#text) return { width: box, height: Math.max(box, line) };
    const m = desktop.rc.measureText(this.#text, font);
    return {
      width: box + theme.metrics.checkLabelGap + Math.ceil(m.width),
      height: Math.max(box, line),
    };
  }

  protected override onEnvironmentChanged(): void {
    this.invalidateMeasure();
  }

  /** Box rectangle and caption rectangle for the current theme. */
  protected layoutParts(theme: Theme): { box: Rect; caption: Rect } {
    const size = this.boxSize(theme);
    const gap = theme.metrics.checkLabelGap;
    const y = Math.round((this.height - size) / 2);
    if (this.boxOnRight) {
      return {
        box: { x: this.width - size, y, width: size, height: size },
        caption: { x: 0, y: 0, width: Math.max(0, this.width - size - gap), height: this.height },
      };
    }
    return {
      box: { x: 0, y, width: size, height: size },
      caption: {
        x: size + gap,
        y: 0,
        width: Math.max(0, this.width - size - gap),
        height: this.height,
      },
    };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    const { box, caption } = this.layoutParts(theme);
    this.drawBox(rc, box, theme, state);

    if (this.#text) {
      drawAlignedText(
        rc,
        this.#text,
        caption,
        this.#font ?? theme.metrics.font,
        state.disabled ? theme.palette.textDisabled : theme.palette.text,
        this.boxOnRight ? "right" : "left",
        "middle",
        true,
      );
    }

    if (state.focused && !state.disabled && this.#text) {
      const m = rc.measureText(this.#text, this.#font ?? theme.metrics.font);
      const ringHeight = Math.min(this.height, Math.ceil(m.height) + 4);
      theme.drawFocusRing(rc, {
        x: caption.x - 2,
        y: Math.round((this.height - ringHeight) / 2),
        width: Math.min(caption.width, Math.ceil(m.width) + 4),
        height: ringHeight,
      });
    }
  }
}
